import { useEffect, useState, type FormEvent } from "react";
import { Link } from "wouter";
import { ChevronRight } from "lucide-react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { paths, INDUSTRIES, INDUSTRY_PRESETS, industryPreset, type Industry, type Tenant } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select, cn } from "@clevis/shared-ui";
import { ListFooter, SearchBox, SortHeader, useListView } from "../../components/ListControls";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";

interface TenantRow extends Tenant {
  id: string;
}

const BILLING_STYLE: Record<string, string> = {
  active: "bg-success/15 text-success",
  trial: "bg-brandorange/15 text-brandorange-dark",
  comped: "bg-ink/10 text-ink",
  past_due: "bg-danger/15 text-danger",
  suspended: "bg-danger/15 text-danger",
  canceled: "bg-steel/15 text-steel",
};

export function AdminCustomers() {
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    name: "",
    slug: "",
    contactEmail: "",
    locationName: "Main Location",
    firstUserEmail: "",
  });
  // Industry → suggested module checklist, each toggleable before create.
  const [industry, setIndustry] = useState<Industry>("general");
  const [moduleFlags, setModuleFlags] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(industryPreset("general").modules.map((m) => [m.key, m.on])),
  );
  const preset = industryPreset(industry);
  const [billing, setBilling] = useState("all");
  const [ind, setInd] = useState("all");

  function pickIndustry(next: Industry) {
    setIndustry(next);
    setModuleFlags(Object.fromEntries(industryPreset(next).modules.map((m) => [m.key, m.on])));
  }

  useEffect(() => {
    const q = query(collection(db, paths.tenants()), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      setTenants(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Tenant) })));
    });
  }, []);

  async function createTenant(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const res = await call<{ tenantId: string; inviteToken: string }>("createTenant", {
        name: form.name,
        slug: form.slug,
        contactEmail: form.contactEmail,
        industry,
        featureFlags: moduleFlags,
        locations: [{ name: form.locationName }],
        firstUser: { email: form.firstUserEmail, role: "owner" },
      });
      setMessage(`Created. First-user invite token: ${res.inviteToken}`);
      setShowCreate(false);
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const view = useListView(tenants, {
    text: (t) => [t.name, t.slug, t.contactEmail, t.contactName, t.industry, t.billingStatus],
    sorts: {
      name: (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }),
      industry: (a, b) => (a.industry ?? "general").localeCompare(b.industry ?? "general"),
      billing: (a, b) => a.billingStatus.localeCompare(b.billingStatus),
      created: (a, b) => b.createdAt - a.createdAt,
    },
    defaultSort: "created",
    filter: (t) => (billing === "all" || t.billingStatus === billing) && (ind === "all" || (t.industry ?? "general") === ind),
  });

  return (
    <div className="mx-auto max-w-5xl space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">
          Customers <span className="text-sm font-normal text-steel">{tenants.length}</span>
        </h1>
        <Button onClick={() => setShowCreate((v) => !v)}>{showCreate ? "Cancel" : "New customer"}</Button>
      </div>

      {showCreate ? (
        <Card>
          <CardTitle>Create customer</CardTitle>
          <form onSubmit={createTenant} className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Slug</Label>
              <Input
                required
                pattern="[a-z0-9-]+"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
            </div>
            <div>
              <Label>Contact email</Label>
              <Input
                type="email"
                required
                value={form.contactEmail}
                onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
              />
            </div>
            <div>
              <Label>First location</Label>
              <Input
                required
                value={form.locationName}
                onChange={(e) => setForm({ ...form, locationName: e.target.value })}
              />
            </div>
            <div>
              <Label>Owner email (gets the invite)</Label>
              <Input
                type="email"
                required
                value={form.firstUserEmail}
                onChange={(e) => setForm({ ...form, firstUserEmail: e.target.value })}
              />
            </div>
            <div>
              <Label>Industry</Label>
              <Select
                value={industry}
                onChange={(e) => pickIndustry(e.target.value as Industry)}
              >
                {INDUSTRIES.map((i) => (
                  <option key={i} value={i}>
                    {INDUSTRY_PRESETS[i].label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="col-span-2 rounded-lg border border-steel/20 bg-cream/60 p-3">
              <p className="mb-2 text-xs font-semibold text-ink">
                What to include for {preset.label.toLowerCase()} — adjust as needed:
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {preset.modules.map((m) => (
                  <label key={m.key} className="flex cursor-pointer items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={moduleFlags[m.key] ?? m.on}
                      onChange={(e) => setModuleFlags({ ...moduleFlags, [m.key]: e.target.checked })}
                    />
                    <span>
                      <span className="font-medium text-ink">{m.label}</span>
                      <span className="block text-xs text-steel">{m.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
              {preset.vocab.tech !== "Tech" || preset.vocab.job !== "Job" ? (
                <p className="mt-2 text-xs text-steel">
                  Vocabulary: workers appear as “{preset.vocab.techs}”, work as “{preset.vocab.jobs}”.
                </p>
              ) : null}
              {preset.planned.length > 0 ? (
                <p className="mt-1 text-xs text-steel">
                  On the roadmap for this industry: {preset.planned.join(", ")}.
                </p>
              ) : null}
            </div>
            <div className="flex items-end">
              <Button type="submit" busy={busy}>
                Create
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {message ? <p className="break-all text-xs text-ink">{message}</p> : null}

      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-steel/10 p-3">
          <SearchBox className="min-w-56 flex-1" value={view.query} onChange={view.setQuery} placeholder="Search company, slug, contact, industry…" />
          <Select className="w-auto py-1.5 text-sm" aria-label="Billing" value={billing} onChange={(e) => setBilling(e.target.value)}>
            <option value="all">Any billing status</option>
            {[...new Set(tenants.map((t) => t.billingStatus))].sort().map((b) => (
              <option key={b} value={b}>
                {b.replace("_", " ")} ({tenants.filter((t) => t.billingStatus === b).length})
              </option>
            ))}
          </Select>
          <Select className="w-auto py-1.5 text-sm" aria-label="Industry" value={ind} onChange={(e) => setInd(e.target.value)}>
            <option value="all">Any industry</option>
            {INDUSTRIES.filter((i) => tenants.some((t) => (t.industry ?? "general") === i)).map((i) => (
              <option key={i} value={i}>
                {INDUSTRY_PRESETS[i].label}
              </option>
            ))}
          </Select>
        </div>
        <div className="hidden grid-cols-[minmax(0,2.4fr)_minmax(0,1.2fr)_120px_110px_16px] items-center gap-3 border-b border-steel/10 px-4 py-2 sm:grid">
          <SortHeader label="Company" k="name" view={view} />
          <SortHeader label="Industry" k="industry" view={view} />
          <SortHeader label="Billing" k="billing" view={view} />
          <SortHeader label="Created" k="created" view={view} />
          <span />
        </div>
        {view.page.map((t) => (
          <Link
            key={t.id}
            href={`/customers/${t.id}`}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 border-b border-steel/10 px-4 py-2 text-sm last:border-0 hover:bg-cream/60 sm:grid-cols-[minmax(0,2.4fr)_minmax(0,1.2fr)_120px_110px_16px] sm:gap-3"
          >
            <span className="min-w-0">
              <span className="block truncate font-medium text-ink">{t.name}</span>
              <span className="block truncate text-[11px] text-steel">
                {t.slug}
                {t.contactEmail ? ` · ${t.contactEmail}` : ""}
              </span>
            </span>
            <span className="hidden truncate text-steel sm:block">{INDUSTRY_PRESETS[(t.industry ?? "general") as Industry]?.label ?? t.industry}</span>
            <span className="hidden sm:block">
              <span className={cn("rounded-full px-2 py-0.5 text-xs", BILLING_STYLE[t.billingStatus] ?? "bg-steel/15 text-ink")}>{t.billingStatus.replace("_", " ")}</span>
            </span>
            <span className="hidden text-xs text-steel sm:block">
              {new Date(t.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            </span>
            <ChevronRight size={14} className="text-steel" />
          </Link>
        ))}
        {view.filtered.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-steel">{tenants.length ? "Nothing matches." : "No customers yet."}</div>
        ) : null}
        <ListFooter shown={view.page.length} matching={view.filtered.length} total={view.total} onMore={view.more} onAll={view.all} noun="companies" />
      </Card>
    </div>
  );
}
